import { useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { supabase } from '../supabase/client';
import { setCalendarDataSelf } from '../Redux/calendarDataSelfSlice';
import { incrementOperation, decrementOperation } from '../Redux/loadingSlice';
import { saveCalendarDataToCache, loadCalendarDataFromCache } from '../data/calendarCache';
import { transformIpCalendarData, transformDonorCalendarData } from '../data/transformers/calendarTransform';
import useUpdateOvulationFlags from './useUpdateOvulationFlags';

/**
 * Hook to load and cache the user's own calendar data.
 *
 * Three-tier cache strategy (checked in order):
 *   1. Redux — if the requested date range is already in calendarDataSelf, use it (no network call)
 *   2. localStorage — if Redux doesn't cover the range, check disk cache.
 *      If covered, populate Redux from cache + do a background Supabase reload.
 *   3. Supabase — if neither cache covers the range, do a blocking RPC call,
 *      write results to Redux and localStorage.
 *
 * Returns reduxCalendar directly — no local state copy.
 *
 * @param {string|null} startMonth - Start month in YYYY-MM format (e.g. '2026-02')
 * @param {string|null} endMonth   - End month in YYYY-MM format (e.g. '2026-08')
 * @returns {{ calendarData, refresh }}
 */
export default function useCalendarDataSelf() {
  const dispatch = useDispatch();

  // ── Redux state (the source of truth for calendar data) ──────────────
  const currentUserId = useSelector((s) => s.user.profile?.id);
  const currentRole = useSelector((s) => s.user.profile?.role);
  const reduxCalendar = useSelector((s) => s.calendarDataSelf.data);       // date-keyed object
  const reduxRangeStart = useSelector((s) => s.calendarDataSelf.range_start); // e.g. '2026-02-01'
  const reduxRangeEnd = useSelector((s) => s.calendarDataSelf.range_end);     // e.g. '2026-08-01'
  const startMonth = useSelector((s) => s.calendar.startMonth);
  const endMonth = useSelector((s) => s.calendar.endMonth);
  const ownedShares = useSelector((s) => s.calendarShares.ownedShares);
  const receivedShares = useSelector((s) => s.calendarShares.receivedShares);

  // ── Memoized shares for dependency tracking ───────────────────────────
  // Only triggers re-run when shares actually change (not just reference)
  // Combines owned and received shares for the key
  const sharesKey = useMemo(() => {
    if (!ownedShares && !receivedShares) {
      return null;
    }
    const allShares = [...Object.values(ownedShares || {}), ...Object.values(receivedShares || {})];
    const key = JSON.stringify(allShares.sort((a, b) => a.id.localeCompare(b.id)));
    return key;
  }, [ownedShares, receivedShares]);

  // Track previous sharesKey to detect changes
  const prevSharesKeyRef = useRef(null);
  const sharesChanged = prevSharesKeyRef.current !== sharesKey && prevSharesKeyRef.current !== null;
  useEffect(() => {
    prevSharesKeyRef.current = sharesKey;
  }, [sharesKey]);

  // ── Calendar data is read directly from Redux (no local state copy) ──

  // ── Date range calculation ───────────────────────────────────────────
  // Uses visible months from Redux. If not set, defaults to ±3 months from today.
  const calculateDateRange = () => {
    let rangeStart, rangeEnd;
    if (startMonth && endMonth) {
      rangeStart = `${startMonth}-01`;
      rangeEnd = `${endMonth}-01`;
    } else {
      const now = new Date();
      const prev = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      const next = new Date(now.getFullYear(), now.getMonth() + 3, 1);
      rangeStart = prev.toISOString().split('T')[0];
      rangeEnd = next.toISOString().split('T')[0];
    }
    return { rangeStart, rangeEnd };
  };

  // ── Range coverage check ─────────────────────────────────────────────
  // Pure function — returns true if the requested range is fully within the cached range.
  const isRangeCovered = (requestedStart, requestedEnd, cachedStart, cachedEnd) => {
    if (!cachedStart || !cachedEnd) return false;
    return requestedStart >= cachedStart && requestedEnd <= cachedEnd;
  };

  // ── RPC data transformer ─────────────────────────────────────────────
  // Converts the raw Supabase RPC response into the date-keyed calendar
  // data structure used by Redux and the UI.
  // - IP users: get_ip_calendar → { day: { ipData: {...}, note, ovulationTests } }
  // - Donor users: get_donor_calendar → { day: { sharedOvulationPredictions, events } }
  const transformRpcData = (rpcData) => {
    if (currentRole === 'ip') {
      return transformIpCalendarData(rpcData);
    } else {
      return transformDonorCalendarData(rpcData);
    }
  };

  // ── Supabase loader ──────────────────────────────────────────────────
  // Calls the appropriate RPC (get_ip_calendar or get_donor_calendar),
  // transforms the response, writes to Redux + localStorage.
  // Always sets global loading state, even during background reloads.
  const loadFromSupabase = async (rangeStart, rangeEnd, isBackground = false) => {
    if (!currentUserId || !currentRole) return;

    // Always set global loading state
    dispatch(incrementOperation('calendarLoad'));

    try {
      const rpcName = currentRole === 'ip' ? 'get_ip_calendar_j' : 'get_donor_calendar_j';
      const { data, error } = await supabase.rpc(rpcName, {
        p_user_id: currentUserId,
        p_range_start: rangeStart,
        p_range_end: rangeEnd,
      });

      if (error) {
        return;
      }

      const transformedData = transformRpcData(data);

      // Update Redux
      dispatch(setCalendarDataSelf({
        data: transformedData,
        range_start: rangeStart,
        range_end: rangeEnd,
      }));

      // Update localStorage
      saveCalendarDataToCache({
        data: transformedData,
        range_start: rangeStart,
        range_end: rangeEnd,
        last_updated: Date.now(),
      });
    } catch (err) {
    } finally {
      // Always decrement global loading state
      dispatch(decrementOperation('calendarLoad'));
    }
  };

  // ── Main load function (three-tier cache) ────────────────────────────
  // Checks Redux → localStorage → Supabase in order.
  const loadCalendarData = async (forceReload = false) => {
    if (!currentUserId || !currentRole) return;

    // Return early if shares not loaded yet (both null)
    if (!ownedShares && !receivedShares) {
      return;
    }


    const { rangeStart, rangeEnd } = calculateDateRange();

    // 1. Check Redux (skip if forceReload is true, e.g., when shares changed)
    const reduxCovered = isRangeCovered(rangeStart, rangeEnd, reduxRangeStart, reduxRangeEnd);

    if (reduxCovered && !forceReload) {
      return;
    }

    // 2. Check localStorage
    const cachedData = loadCalendarDataFromCache();

    if (cachedData) {
      const localCovered = isRangeCovered(rangeStart, rangeEnd, cachedData.range_start, cachedData.range_end);

      if (localCovered) {
        dispatch(setCalendarDataSelf({
          data: cachedData.data,
          range_start: cachedData.range_start,
          range_end: cachedData.range_end,
        }));

        // Background reload from Supabase (shows loading indicator)
        loadFromSupabase(rangeStart, rangeEnd, true);
        return;
      }
    }

    // 3. Load from Supabase (blocking)
    await loadFromSupabase(rangeStart, rangeEnd, false);
  };

  // ── Trigger: load calendar when user/role/range/shares changes ──────────────
  // loadCalendarData is NOT in deps — it would cause re-runs after optimistic
  // updates. Only the actual trigger values (user, role, visible months, shares) are deps.
  // Uses sharesKey (memoized) to only reload when shares actually change, not on every render.
  // When shares change, force a reload to bypass Redux cache.
  useEffect(() => {
    loadCalendarData(sharesChanged);
  }, [currentUserId, currentRole, startMonth, endMonth, sharesKey]);


  // ── Update ovulation flags in DB ─────────────────────────────────────
  // Async update of ovulation_actual and ovulation_prediction flags
  // for sharing with donors via RPC. Watches predictionUpdateCounter
  // and only runs when period days or ovulation tests change.
  useUpdateOvulationFlags();

  // ── Manual refresh — explicitly triggered by user, not by Redux changes ──
  const refresh = () => {
    const { rangeStart, rangeEnd } = calculateDateRange();
    loadFromSupabase(rangeStart, rangeEnd, false);
  };

  return { calendarData: reduxCalendar, refresh };
}
